<template>
    <!-- a playoffs or cup match: its bracket instead of the ranking -->
    <div v-if="bracket" class="bst-bracket">
        <!-- a small bracket (4 teams) is enlarged to the width of the ranking card -->
        <div ref="board" :style="{ zoom: bracketZoom }">
            <bracket-board :bracket="bracket" :link-matches="false" :responsive="false" :card-width="204" />
        </div>
    </div>
    <broadcast-card v-else :eyebrow="eyebrow" title="Βαθμολογία">
        <div class="bst" :class="{ 'bst--dense': rows.length > 8 }">
            <div class="bst-row bst-row--head">
                <span>#</span>
                <span class="bst-team">Ομάδα</span>
                <span>Β</span>
                <span>Α</span>
                <span>Ν</span>
                <span>Η</span>
                <span>+/-</span>
                <span>Φόρμα</span>
            </div>
            <div
                v-for="row in rows"
                :key="row.key"
                class="bst-row"
                :class="{ 'bst-row--match': row.color }"
                :style="row.color ? { '--team-color': row.color } : null"
            >
                <span class="bst-pos" :class="{ 'bst-pos--leader': row.position === 1 }">{{ row.position }}</span>
                <span class="bst-team">
                    <span class="bst-team__logo"><img v-if="row.logo" :src="row.logo" alt=""></span>
                    <span class="bst-team__name">{{ row.name }}</span>
                </span>
                <span class="bst-points">{{ row.points }}</span>
                <span>{{ row.played }}</span>
                <span>{{ row.won }}</span>
                <span>{{ row.lost }}</span>
                <span class="bst-diff" :class="{ 'bst-diff--plus': row.diff > 0, 'bst-diff--minus': row.diff < 0 }">{{ row.diff > 0 ? `+${row.diff}` : row.diff }}</span>
                <span class="bst-form">
                    <i v-for="(result, index) in row.form" :key="index" :class="result === 'W' ? 'bst-form--win' : 'bst-form--loss'">{{ result === 'W' ? 'Ν' : 'Η' }}</i>
                    <em v-if="!row.form.length">–</em>
                </span>
            </div>
        </div>
    </broadcast-card>
</template>

<script>
    import BroadcastCard from '@/components/BroadcastCard'
    import BracketBoard from '@/components/BracketBoard'
    import { getLastFiveForm, getTeamId } from '@/utils/standings'
    import { BACKGROUNDS, getMatchColors } from '@/utils/colors'

    const ASSETS_URL = 'https://storage.googleapis.com'
    const PHASES = { placements: 'Προκριματικά', regular_season: 'Κανονική περίοδος', playoffs: 'Playoffs' }

    // Pregame / full time: the group's ranking, with the two teams of the match marked in their colours
    export default {
        name: 'MatchStandingsComponent',
        components: { BroadcastCard, BracketBoard },
        props: {
            match: {
                type: Object,
                default: () => ({})
            },
            ranking: {
                type: Array,
                default: () => []
            },
            // the bracket of the playoffs or the cup of the match, when it is one
            bracket: {
                type: Object,
                default: null
            }
        },
        data() {
            return {
                bracketZoom: 1
            }
        },
        computed: {
            eyebrow() {
                const competition = this.match.cupLeague || this.match.league
                const league = competition && (competition.title || competition.name)
                const phase = PHASES[this.match.phase]
                return [league, phase].filter(Boolean).join(' · ')
            },
            rows() {
                const colors = getMatchColors(this.match.team1, this.match.team2, BACKGROUNDS.dark)
                const team1Id = this.match.team1 && this.match.team1.id
                const team2Id = this.match.team2 && this.match.team2.id
                return this.ranking.map((item, index) => {
                    const id = getTeamId(item)
                    const won = Number(item.won) || 0
                    const lost = Number(item.lost) || 0
                    // the games of the ranking (a forfeit counts there too), else wins and losses
                    let color = null
                    if (id && String(id) === String(team1Id)) color = colors.team1
                    if (id && String(id) === String(team2Id)) color = colors.team2
                    return {
                        key: id || index,
                        position: item.position || index + 1,
                        name: item.name,
                        logo: item.imageUrl ? `${ASSETS_URL}/${item.imageUrl}` : null,
                        points: Number(item.standing) || 0,
                        played: Number(item.gp) || won + lost,
                        won,
                        lost,
                        diff: Number(item.pointsDiff) || 0,
                        form: getLastFiveForm(item),
                        color
                    }
                })
            }
        },
        watch: {
            bracket: {
                immediate: true,
                handler() {
                    this.$nextTick(this.fitBracket)
                }
            }
        },
        methods: {
            // as wide as the ranking card (1150px), as long as it fits the height of the screen
            fitBracket() {
                const board = this.$refs.board
                if (!board) return
                this.bracketZoom = 1
                this.$nextTick(() => {
                    const width = board.scrollWidth
                    const height = board.scrollHeight
                    if (!width || !height) return
                    const room = Math.min(1150, window.innerWidth - 760)
                    this.bracketZoom = Math.max(1, Math.min(room / width, (window.innerHeight - 80) / height, 2))
                })
            }
        }
    }
</script>

<style scoped>
/* the room of the broadcast card: clear of the match info on the left */
.bst-bracket {
    max-width: calc(100vw - 760px);
    transform: translateX(300px);
}

/* tighter rounds: the eight teams fit 1160px (a 1920px screen) with room for the names */
.bst-bracket ::v-deep .bb {
    --bb-gap: 20px;
}

@media (max-width: 960px) {
    .bst-bracket {
        max-width: 94vw;
        transform: translateX(3%);
    }
}

.bst {
    font-variant-numeric: tabular-nums;
}

.bst-row {
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr) 80px 70px 70px 70px 90px 200px;
    align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    border-left: 6px solid transparent;
    color: #fff;
    font-size: 1.6rem;
    font-weight: 700;
    text-align: center;
}

.bst-row--head {
    border-bottom: 2px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.62);
    font-size: 1.05rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

/* the two teams of the match */
.bst-row--match {
    border-left-color: var(--team-color);
    border-radius: 10px;
    background: rgba(3, 229, 183, 0.12);
}

.bst-pos {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    margin: 0 auto;
    border-radius: 50%;
    background: #2e2e2e;
    font-size: 1.3rem;
    font-weight: 800;
}

.bst-pos--leader {
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    color: #fff;
}

.bst-team {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
    text-align: left;
}

.bst-team__logo {
    overflow: hidden;
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 58px;
    height: 58px;
    padding: 4px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.14);
}

.bst-team__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.bst-team__name {
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.7rem;
    font-weight: 400;
    letter-spacing: 0.02em;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.bst-points {
    color: #fff;
    font-size: 1.9rem;
    font-weight: 800;
}

.bst-diff--plus {
    color: #00b894;
}

.bst-diff--minus {
    color: #f44336;
}

.bst-form {
    display: inline-flex;
    justify-content: center;
    gap: 6px;
}

.bst-form i {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    color: #fff;
    font-size: 0.95rem;
    font-style: normal;
    font-weight: 800;
}

.bst-form--win {
    background: #00b894;
}

.bst-form--loss {
    background: #f44336;
}

.bst-form em {
    color: rgba(255, 255, 255, 0.42);
    font-style: normal;
}

/* bigger groups: tighter rows so the whole table fits the screen */
.bst--dense .bst-row {
    padding: 5px 12px;
    font-size: 1.35rem;
}

.bst--dense .bst-team__logo {
    width: 44px;
    height: 44px;
}

.bst--dense .bst-team__name {
    font-size: 1.4rem;
}
</style>
